import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { Socket } from "socket.io-client/build/esm/socket";
import { getStructuresById, TournamentResponse } from "../../../api/tournament";

export const useTimer = (
  tournamentId: string | undefined,
  pubId: number | undefined,
) => {
  const socketRef = useRef<Socket | null>(null);
  const [data, setData] = useState<TournamentResponse | null>(null);
  const [canUsed, setCanUsed] = useState(true);
  const pollIntervalRef = useRef<NodeJS.Timer | null>(null); // api 호출 진행중 여부

  // api 호출 함수
  const startPolling = () => {
    if (pollIntervalRef.current || !tournamentId) return; // 진행중이면 무시
    pollIntervalRef.current = setInterval(async () => {
      try {
        const result = await getStructuresById(+tournamentId);
        setData(result);
      } catch (err) {
        console.error(err);
      }
    }, 1000);
  };

  // api 호출 종료 함수
  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  // 소켓 연결
  const connectSocket = () => {
    if (!tournamentId || !pubId) return;
    if (socketRef.current?.connected) return; // 이미 연결되어있다면 return

    // 혹시 남아있는 소켓 제거
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    socketRef.current = io(`${process.env.REACT_APP_WEB_HOST}`, {
      transports: ["websocket"],
      path: "/socket.io",
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    // 셀러 검즘
    socketRef.current?.on("joined-result", (result: boolean) => {
      setCanUsed(result);
    });

    // 데이터 호출
    socketRef.current.on("send-detail", (data: TournamentResponse) => {
      setData(data);
    });

    // 연결 성공 → api 호출 중지 및 방 참여
    socketRef.current.on("connect", () => {
      stopPolling();
      socketRef.current?.emit("join-room", { tournamentId, pubId });
    });

    // 연결 끊기거나 에러 발생 → api 호출 시작
    socketRef.current.on("disconnect", () => {
      if (tournamentId) startPolling();
    });

    socketRef.current.on("connect_error", () => {
      if (tournamentId) startPolling();
    });
  };

  const leaveSocket = () => {
    if (!socketRef.current) return;
    socketRef.current.emit("leave-room", { tournamentId, pubId });
  };

  const disconnectSocket = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    stopPolling();
  };

  // 소켓 끊겼을 시 연결 시도
  useEffect(() => {
    connectSocket();

    const interval = setInterval(() => {
      if (socketRef.current && !socketRef.current.connected) {
        connectSocket();
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      disconnectSocket();
    };
  }, []);

  return {
    // 소켓
    socketRef,
    // 소켓 관련 함수
    connectSocket,
    disconnectSocket,
    leaveSocket,
    // 토너먼트 데이터
    data,
    setData,
    // 셀러 검증
    canUsed,
  };
};
